import { Component } from '@theme/component';

const ORDER_PARAM = 'siparis';
const REQUEST_TIMEOUT = 15_000;
const COPIED_RESET_DELAY = 2_000;

/** Index of the current step on the timeline for each stage. */
const STAGE_STEP = {
  awaiting_payment: 0,
  preparing: 1,
  partially_shipped: 2,
  shipped: 2,
  delivered: 4,
};

/**
 * @typedef {object} Shipment
 * @property {string} shippedAt
 * @property {string | null} deliveredAt
 * @property {string | null} estimatedDeliveryAt
 * @property {string} company
 * @property {string} number
 * @property {string} url
 */

/**
 * @typedef {object} TrackedOrder
 * @property {string} name
 * @property {string} createdAt
 * @property {keyof typeof STAGE_STEP | 'cancelled' | 'refunded'} stage
 * @property {'card' | 'cash_on_delivery' | 'bank_transfer' | 'other'} payment
 * @property {boolean} paymentPending
 * @property {boolean} partiallyRefunded
 * @property {boolean} returnInProgress
 * @property {{ amount: string, currency: string } | null} total
 * @property {string} city
 * @property {Shipment[]} shipments
 * @property {{ title: string, variant: string, quantity: number, removed: boolean, image: string, imageAlt: string, url: string }[]} items
 */

/**
 * @typedef {object} SwistOrderTrackingRefs
 * @property {HTMLElement} formPanel - Wraps the lookup form.
 * @property {HTMLFormElement} form - Order number + e-mail form.
 * @property {HTMLInputElement} orderInput - Order number field.
 * @property {HTMLInputElement} emailInput - E-mail field.
 * @property {HTMLElement} error - The form's alert region.
 * @property {HTMLButtonElement} submitButton - Starts the lookup.
 * @property {HTMLElement} submitLabel - Text inside the submit button.
 * @property {HTMLElement} result - Receives the rendered order.
 * @property {HTMLTemplateElement} resultTemplate - Status, timeline, items and summary.
 * @property {HTMLTemplateElement} shipmentTemplate - One shipment with its tracking link.
 * @property {HTMLTemplateElement} itemTemplate - One ordered item.
 * @property {HTMLScriptElement} labels - Translated strings as JSON.
 * @property {HTMLAnchorElement} [whatsappLink] - Help band WhatsApp link, prefilled with the order.
 */

/**
 * Order tracking page: looks an order up by number and e-mail through the order lookup
 * service and renders its status, timeline, shipments and items.
 *
 * @extends {Component<SwistOrderTrackingRefs>}
 */
class SwistOrderTrackingComponent extends Component {
  requiredRefs = [
    'formPanel',
    'form',
    'orderInput',
    'emailInput',
    'error',
    'submitButton',
    'submitLabel',
    'result',
    'resultTemplate',
    'shipmentTemplate',
    'itemTemplate',
    'labels',
  ];

  /** @type {AbortController | null} */
  #request = null;

  /** @type {number | undefined} */
  #copiedTimer;

  /** @type {any} */
  #labels = {};

  connectedCallback() {
    super.connectedCallback();
    this.#readLabels();
    this.#prefill();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#readLabels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
    clearTimeout(this.#copiedTimer);
  }

  /** @param {SubmitEvent} event */
  handleSubmit = async (event) => {
    event.preventDefault();

    const { orderInput, emailInput } = this.refs;
    const order = orderInput.value.trim();
    const email = emailInput.value.trim();

    if (!/^(?:#|swt)?\s*\d{3,8}$/i.test(order.replace(/[\s-]/g, ''))) {
      this.#showError('invalid_order', orderInput);
      return;
    }
    if (!emailInput.checkValidity() || !email) {
      this.#showError('invalid_email', emailInput);
      return;
    }

    this.clearError();
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    const timeout = window.setTimeout(() => request.abort(), REQUEST_TIMEOUT);
    this.#setBusy(true);

    try {
      const response = await fetch(this.dataset.endpoint ?? '', {
        method: 'POST',
        // text/plain keeps this a simple request (no CORS preflight).
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({ order, email }),
        signal: request.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (request !== this.#request) return;

      if (response.ok && data.ok && data.order) {
        this.#renderOrder(data.order);
        return;
      }

      const code = data.error;
      if (code === 'invalid_order') this.#showError('invalid_order', orderInput);
      else if (code === 'invalid_email') this.#showError('invalid_email', emailInput);
      else if (code === 'not_found') this.#showError('not_found');
      else if (response.status === 429) this.#showError('too_many_attempts');
      else this.#showError('unavailable');
    } catch {
      if (request === this.#request) this.#showError('unavailable');
    } finally {
      clearTimeout(timeout);
      if (request === this.#request) {
        this.#request = null;
        this.#setBusy(false);
      }
    }
  };

  clearError = () => {
    const { error, orderInput, emailInput } = this.refs;
    error.textContent = '';
    orderInput.removeAttribute('aria-invalid');
    emailInput.removeAttribute('aria-invalid');
    orderInput.removeAttribute('aria-errormessage');
    emailInput.removeAttribute('aria-errormessage');
  };

  startOver = () => {
    const { result, formPanel, orderInput } = this.refs;
    result.hidden = true;
    result.replaceChildren();
    formPanel.hidden = false;
    this.#setOrderParam('');
    this.#updateWhatsapp('');
    orderInput.select();
    orderInput.focus();
  };

  /** @param {MouseEvent} event */
  copyTracking = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    const number = button?.dataset.number;
    if (!button || !number) return;

    try {
      await navigator.clipboard.writeText(number);
    } catch {
      return;
    }

    button.textContent = this.#labels.copied;
    clearTimeout(this.#copiedTimer);
    this.#copiedTimer = window.setTimeout(() => {
      button.textContent = this.#labels.copy;
    }, COPIED_RESET_DELAY);
  };

  #readLabels() {
    try {
      this.#labels = JSON.parse(this.refs.labels.textContent || '{}');
    } catch {
      this.#labels = {};
    }
  }

  /** Fills the order number from ?siparis= and looks it up right away for signed-in customers. */
  #prefill() {
    const order = new URL(window.location.href).searchParams.get(ORDER_PARAM);
    if (!order) return;

    const { orderInput, emailInput, form } = this.refs;
    orderInput.value = order.slice(0, 20);

    if (this.dataset.customer && emailInput.value) form.requestSubmit();
    else emailInput.focus();
  }

  /**
   * @param {string} code
   * @param {HTMLInputElement} [field]
   */
  #showError(code, field) {
    const { error } = this.refs;
    error.textContent = this.#labels.errors?.[code] ?? '';
    if (!field) return;

    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-errormessage', error.id);
    field.focus();
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const { submitButton, submitLabel } = this.refs;
    submitButton.setAttribute('aria-busy', String(busy));
    submitButton.disabled = busy;
    submitLabel.textContent = busy ? this.#labels.loading : this.#labels.submit;
  }

  /** @param {TrackedOrder} order */
  #renderOrder(order) {
    const { result, resultTemplate, formPanel } = this.refs;
    const fragment = /** @type {DocumentFragment} */ (resultTemplate.content.cloneNode(true));
    const field = (/** @type {string} */ name) => fragment.querySelector(`[data-field="${name}"]`);

    const stage = this.#labels.stages?.[order.stage] ?? { title: '', text: '' };
    const statusCard = field('statusCard');
    if (statusCard instanceof HTMLElement) statusCard.dataset.stage = order.stage;
    setText(field('orderName'), order.name);
    setText(field('statusTitle'), stage.title);
    setText(field('statusText'), stage.text);

    const orderDate = field('orderDate');
    if (orderDate instanceof HTMLTimeElement) {
      orderDate.dateTime = order.createdAt;
      orderDate.textContent = this.#formatDate(order.createdAt);
    }

    this.#renderNotes(field('notes'), order);
    this.#renderSteps(field('steps'), order);
    this.#renderShipments(field('shipments'), order);
    this.#renderItems(field('items'), order);

    setText(field('payment'), this.#labels.payment?.[order.payment] ?? '');
    setText(field('city'), order.city);
    toggle(field('cityRow'), Boolean(order.city));
    const showTotal = order.total && order.stage !== 'cancelled' && order.stage !== 'refunded';
    toggle(field('totalRow'), Boolean(showTotal));
    if (showTotal && order.total) setText(field('total'), this.#formatMoney(order.total));

    result.replaceChildren(fragment);
    formPanel.hidden = true;
    result.hidden = false;

    this.#setOrderParam(order.name);
    this.#updateWhatsapp(order.name);

    const title = result.querySelector('.swist-ot__status-title');
    if (title instanceof HTMLElement) {
      title.focus({ preventScroll: true });
      title.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    window.Shopify?.analytics?.publish?.('swist_order_tracking_lookup', { stage: order.stage });
  }

  /**
   * @param {Element | null} element
   * @param {TrackedOrder} order
   */
  #renderNotes(element, order) {
    if (!(element instanceof HTMLElement)) return;

    const notes = [];
    if (order.returnInProgress) notes.push(this.#labels.notes?.return_in_progress);
    if (order.partiallyRefunded) notes.push(this.#labels.notes?.partially_refunded);
    if (order.payment === 'cash_on_delivery' && order.stage !== 'cancelled' && order.stage !== 'refunded') {
      notes.push(this.#labels.notes?.cash_on_delivery);
    }

    const text = notes.filter(Boolean).join(' ');
    element.textContent = text;
    element.hidden = !text;
  }

  /**
   * @param {Element | null} list
   * @param {TrackedOrder} order
   */
  #renderSteps(list, order) {
    if (!list) return;

    const current = STAGE_STEP[/** @type {keyof typeof STAGE_STEP} */ (order.stage)] ?? 0;
    const firstShipment = order.shipments[0];
    const lastDelivery = order.shipments.map((shipment) => shipment.deliveredAt).filter(Boolean).sort().pop();
    const dates = [order.createdAt, '', firstShipment?.shippedAt ?? '', order.stage === 'delivered' ? lastDelivery ?? '' : ''];

    list.querySelectorAll('.swist-ot__step').forEach((step, index) => {
      if (!(step instanceof HTMLElement)) return;

      const state = index < current ? 'done' : index === current ? 'current' : 'upcoming';
      step.dataset.state = state;
      if (state === 'current') step.setAttribute('aria-current', 'step');
      setText(step.querySelector('.swist-ot__step-state'), this.#labels.stepStates?.[state] ?? '');

      const time = step.querySelector('.swist-ot__step-date');
      const date = dates[index];
      if (time instanceof HTMLTimeElement && date && index <= current) {
        time.dateTime = date;
        time.textContent = this.#formatDate(date, true);
      }
    });
  }

  /**
   * @param {Element | null} container
   * @param {TrackedOrder} order
   */
  #renderShipments(container, order) {
    if (!(container instanceof HTMLElement) || !order.shipments.length) return;
    if (order.stage === 'cancelled') return;

    const numbered = order.shipments.length > 1;
    order.shipments.forEach((shipment, index) => {
      const fragment = /** @type {DocumentFragment} */ (this.refs.shipmentTemplate.content.cloneNode(true));
      const field = (/** @type {string} */ name) => fragment.querySelector(`[data-field="${name}"]`);

      setText(
        field('title'),
        numbered ? this.#labels.shipmentNumbered.replace('__NUMBER__', String(index + 1)) : this.#labels.shipment
      );
      setText(field('company'), shipment.company);

      const dateParts = [this.#labels.shippedOn.replace('__DATE__', this.#formatDate(shipment.shippedAt))];
      if (shipment.estimatedDeliveryAt && !shipment.deliveredAt) {
        dateParts.push(this.#labels.estimatedDelivery.replace('__DATE__', this.#formatDate(shipment.estimatedDeliveryAt)));
      }
      setText(field('date'), dateParts.join(' · '));

      setText(field('number'), shipment.number);
      toggle(field('trackingRow'), Boolean(shipment.number));
      const copy = field('copy');
      if (copy instanceof HTMLButtonElement) {
        copy.dataset.number = shipment.number;
        copy.hidden = !navigator.clipboard;
      }

      const link = field('trackLink');
      if (link instanceof HTMLAnchorElement) {
        if (shipment.url) link.href = shipment.url;
        else link.hidden = true;
      }

      container.append(fragment);
    });
    container.hidden = false;
  }

  /**
   * @param {Element | null} list
   * @param {TrackedOrder} order
   */
  #renderItems(list, order) {
    if (!list) return;

    for (const item of order.items) {
      const fragment = /** @type {DocumentFragment} */ (this.refs.itemTemplate.content.cloneNode(true));
      const field = (/** @type {string} */ name) => fragment.querySelector(`[data-field="${name}"]`);

      const row = fragment.querySelector('li');
      if (item.removed && row) row.dataset.removed = '';

      const image = field('image');
      if (image instanceof HTMLImageElement) {
        if (item.image) {
          image.src = item.image;
          image.alt = item.imageAlt;
        } else {
          image.remove();
        }
      }

      const title = field('title');
      setText(title, item.title);
      if (title instanceof HTMLAnchorElement && item.url) title.href = item.url;

      setText(field('variant'), item.variant);
      const quantity = this.#labels.quantity.replace('__QUANTITY__', String(item.quantity));
      setText(field('quantity'), item.removed ? `${quantity} · ${this.#labels.removed}` : quantity);

      list.append(fragment);
    }
  }

  /** @param {string} orderName */
  #setOrderParam(orderName) {
    const url = new URL(window.location.href);
    if (orderName) url.searchParams.set(ORDER_PARAM, orderName);
    else url.searchParams.delete(ORDER_PARAM);
    history.replaceState(history.state, '', url);
  }

  /** @param {string} orderName */
  #updateWhatsapp(orderName) {
    const link = this.refs.whatsappLink;
    const number = this.dataset.whatsapp;
    if (!link || !number) return;

    // The server-rendered link carries the generic message; keep it for "start over".
    link.dataset.defaultHref ??= link.href;
    if (!orderName) {
      link.href = link.dataset.defaultHref;
      return;
    }

    const url = new URL(`https://wa.me/${number}`);
    url.searchParams.set('text', this.#labels.whatsappOrder.replace('__ORDER__', orderName));
    link.href = url.toString();
  }

  /**
   * @param {string} value
   * @param {boolean} [short]
   */
  #formatDate(value, short = false) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'tr', {
      day: 'numeric',
      month: short ? 'short' : 'long',
      year: short ? undefined : 'numeric',
      timeZone: 'Europe/Istanbul',
    }).format(date);
  }

  /** @param {{ amount: string, currency: string }} money */
  #formatMoney(money) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'tr', {
        style: 'currency',
        currency: money.currency,
      }).format(Number(money.amount));
    } catch {
      return `${money.amount} ${money.currency}`;
    }
  }
}

/**
 * @param {Element | null} element
 * @param {string} text
 */
function setText(element, text) {
  if (element) element.textContent = text;
}

/**
 * @param {Element | null} element
 * @param {boolean} visible
 */
function toggle(element, visible) {
  if (element instanceof HTMLElement) element.hidden = !visible;
}

if (!customElements.get('swist-order-tracking-component')) {
  customElements.define('swist-order-tracking-component', SwistOrderTrackingComponent);
}
